// src/lib/abTest.js
// Client-side A/B testing — deterministic 50/50 sticky assignment.
//
// Persistence: localStorage key "ab_variant" AND cookie "ab_variant" (30-day),
// so the variant is sticky across pages, sessions and (via cookie) subdomains.
//
// SSR / react-snap prerender safety: useAbVariant() starts at the control
// variant ('A') so prerendered HTML and first client render match; the real
// (possibly 'B') assignment is applied in an effect via the `ready` flag,
// which the caller uses to fire a single ab_impression event.

import { useEffect, useState } from 'react';

const KEY = 'ab_variant';
const COOKIE_DAYS = 30;

function setCookie(name, value, days) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {
    /* document unavailable (prerender) — ignore */
  }
}

function getCookie(name) {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/** Read the stored variant ('A' | 'B') or null if not yet assigned. */
export function getAbVariant() {
  if (typeof window === 'undefined') return null;
  let v = null;
  try {
    v = window.localStorage.getItem(KEY);
  } catch {
    /* localStorage blocked — fall through to cookie */
  }
  return v || getCookie(KEY);
}

/** Assign (once) and persist a sticky 50/50 variant, returning 'A' | 'B'. */
export function assignAbVariant() {
  if (typeof window === 'undefined') return 'A';
  let v = getAbVariant();
  if (v !== 'A' && v !== 'B') {
    v = Math.random() < 0.5 ? 'A' : 'B';
  }
  try {
    window.localStorage.setItem(KEY, v);
  } catch {
    /* ignore — cookie still persists it */
  }
  setCookie(KEY, v, COOKIE_DAYS);
  return v;
}

/**
 * Hook returning { variant, ready }. `variant` is the control ('A') until the
 * client-side assignment runs on mount, after which `ready` flips to true.
 * Callers fire ab_impression when `ready` becomes true (see HomePage).
 */
export function useAbVariant() {
  const [state, setState] = useState({ variant: 'A', ready: false });

  useEffect(() => {
    setState({ variant: assignAbVariant(), ready: true });
  }, []);

  return state;
}
